import React from 'react';
import { ChangeEvent, FunctionComponent, KeyboardEventHandler, useEffect, useState } from 'react';
import { IFeatureFlagRepository } from '@delta-defense/feature-flag-service/service/iFeatureFlagRepository';
import { Field, ITransaction } from '../../core/domain/module';
import { IAnalytics } from '../../core/services/analytics/module';
import { IBundle, IGiveaways, ISubscriptions } from '../../core/services/module';
import { FieldTypes } from '../../configuration/fieldTypes';
import { getGiveawayId } from '../../utilities/getGiveawayId';
import { AddSpouseCheckbox } from './AddSpouseCheckbox';
import { BillingFrequency } from './BillingFrequency';
import { CheckboxField } from './CheckboxField';
import { InputField } from './InputField';
import { LevelSelectionChart } from './LevelSelectionChart';
import { StateSelectField } from './StateSelectField';
import { UseCardOnFile } from './UseCardOnFile';
import { SimpleLevel } from './SimpleLevel';

export const FieldTypesRecord: Record<FieldTypes, FunctionComponent<TransactionFieldProps>> = {
	[FieldTypes.AddSpouseCheckbox]: AddSpouseCheckbox,
	[FieldTypes.BillingFrequency]: BillingFrequency,
	[FieldTypes.Checkbox]: CheckboxField,
	[FieldTypes.Email]: InputField,
	[FieldTypes.Number]: InputField,
	[FieldTypes.Password]: InputField,
	[FieldTypes.Phone]: InputField,
	[FieldTypes.PostalCode]: InputField,
	[FieldTypes.Text]: InputField,
	[FieldTypes.StateSelect]: StateSelectField,
	[FieldTypes.LevelSelectionChart]: LevelSelectionChart,
	[FieldTypes.SimpleLevel]: SimpleLevel,
	[FieldTypes.UseCardOnFile]: UseCardOnFile,
	[FieldTypes.VerificationCode]: InputField,
	[FieldTypes.AddressLine1]: InputField,
	[FieldTypes.AddressLine2]: InputField,
	[FieldTypes.City]: InputField
};

export type TransactionFieldProps = {
	field: Field;
	disabled?: boolean;
	onBlur?: React.FocusEventHandler;
	className?: string;
	elegantStyling?: boolean;
	defaultValue?: string;
	readOnly?: boolean;
	labelRef?: any;
	showValidation?: boolean;
	SubmitButtonComponent?: FunctionComponent<any>;
	transaction?: ITransaction;
	subscriptions?: ISubscriptions;
	bundleIdService?: IBundle;
	analytics?: IAnalytics;
	changeHandler?: (event: ChangeEvent<HTMLElement>) => void;
	onKeyDown?: KeyboardEventHandler;
	giveaways?: IGiveaways;
	getGiveawayIdFunc?: typeof getGiveawayId;
	useRefFunc?: typeof React.useRef;
	useEffectFunc?: typeof React.useEffect;
	featureFlags?: IFeatureFlagRepository
};

export const TransactionField: FunctionComponent<TransactionFieldProps> = (props) => {
	const [validity, setValidity] = useState<boolean>(true);
	const [_now, setNow] = useState(0);
	const { field, defaultValue } = props;
	const elegantStyling = props.elegantStyling;

	useEffect(() => {
		if (!field.GetValue() && defaultValue) {
			field.SetValue(defaultValue);
		}
	}, []);

	useEffect(() => {
		const subscription = field.Validity.Subscribe((validity) => {
			setValidity(!!validity);
			setNow(Date.now() + Math.random());
		}, false);

		return () => {
			field.Validity.Cancel(subscription);
		};
	}, [field]);

	const FieldComponent = FieldTypesRecord[field.Type];
	const showValidation = !validity && !field.Clean;

	return <FieldComponent key={`${field.Key}_${defaultValue}`} {...{ ...props, elegantStyling }} showValidation={showValidation} />;
};
